import { useState } from "react";
import axios from "axios";
import DebouncedOrderSearch from "../components/DebouncedOrderSearch";
import { useCalendar } from "../contexts/CalendarContext";
import { IClosedOrder } from "../types/calendar";

/**
 * Order-number lookup that jumps the calendar to that order's settlement date.
 *
 * Extracted from CalendarFilters so it can render in two places: inline in the
 * desktop filter bar, or as the floating bottom bar on mobile.
 */
export default function CalendarOrderSearch({
  onGoToDate,
  textboxLabel,
  textboxPlaceholder
}: {
  onGoToDate: (date: string) => void;
  textboxLabel?: string;
  textboxPlaceholder?: string;
}) {
  const { showToastMessage } = useCalendar();
  const [searchOrderValue, setSearchOrderValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  return (
    <DebouncedOrderSearch
      size="small"
      disabled={isSearching}
      textboxLabel={textboxLabel}
      textboxPlaceholder={textboxPlaceholder}
      // I hate the way I did this too, but this component drills down to a highly
      // used reusable component that would have been a hassle to change around
      value={isSearching ? "Searching..." : searchOrderValue}
      onSelect={async (_e: React.SyntheticEvent<Element, Event>, option: string | null) => {
        setSearchOrderValue(option || "");
        if (!option) {
          return;
        }

        setIsSearching(true);
        const { data }: { data: IClosedOrder } = await axios.get(
          `/api/calendar/getclosedorder?orderNumber=${option}`
        );
        setIsSearching(false);

        if (!data) {
          showToastMessage({
            message: "Something went wrong. No data found for this order",
            severity: "error"
          });
          return;
        }

        if (!data.settlementDate) {
          showToastMessage({
            message: "This order has no settlement date",
            severity: "error"
          });
          return;
        }

        onGoToDate(data.settlementDate || "");
        showToastMessage({
          message: "Order may not be visible due to filters settings",
          severity: "warning"
        });
      }}
    />
  );
}
